import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { forgotPassword } from '../api/auth';
import { Illustration } from '../components/common/Illustration';

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await forgotPassword(email);
      setSent(true);
      setDevToken(res.devResetToken ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-card glass-surface">
        <Illustration context="vault.identity" size="sm" style={{ margin: '0 auto 16px auto', display: 'block', maxHeight: '120px' }} />
        <span className="section-kicker">Reset password</span>
        <h1>Forgot your password?</h1>
        <p>Enter your email and we'll send you a link to reset it.</p>

        {sent ? (
          <div className="auth-form">
            <div className="acc-success">
              If an account exists for that email, a reset link has been sent.
            </div>
            {devToken && (
              <div className="dev-reset-note">
                <p>Development mode (no email service): continue with your reset link below.</p>
                <Link className="primary-action" to={`/reset-password?token=${devToken}`}>
                  Continue to reset
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>
        ) : (
          <form className="auth-form" onSubmit={onSubmit}>
            {error && (
              <div className="auth-error" role="alert">
                {error}
              </div>
            )}
            <label className="auth-field">
              <Mail size={17} aria-hidden="true" />
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <button type="submit" className="primary-action" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
              {!submitting && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>
        )}

        <div className="auth-links">
          <Link to="/login">Back to log in</Link>
        </div>
      </div>
    </section>
  );
}
